/**
 * The domain somebody actually registered, out of a hostname.
 *
 * A case is one site, and a site is what its owner pays for once a year —
 * `nudogram.com`, not `hu.nudogram.com` and `cn.nudogram.com` and
 * `pl.nudogram.com` as three separate pieces of work. Pirates spin subdomains
 * per language and per mirror precisely because it multiplies the effort of
 * complaining; grouping by hostname made us do that multiplication to
 * ourselves, and the dev backfill turned one operator into eleven cases.
 *
 * The naive rule — take the last two labels — is wrong in the direction that
 * matters. `something.co.uk` would reduce to `co.uk`, and two unrelated
 * British sites, possibly belonging to two different clients, would collapse
 * into one case with one notice. The multi-part suffixes below are the ones
 * that actually occur in our own data (checked against dev: `co.uk`, `com.br`,
 * `co.jp`, `gov.ua`, `ac.uk`, `com.cn`, `edu.cn`, `co.nz`, `com.my`, `gov.in`,
 * `gov.do`, `gov.uk`) plus the rest of the common set, because the failure is
 * silent and the cost of a longer list is nothing.
 *
 * This is not the full public suffix list, deliberately: that is nine thousand
 * lines that need updating, and every entry we are missing degrades to "group
 * one label less aggressively", which splits a case rather than merging two.
 * Splitting is recoverable by a person; merging two clients' sites into one
 * notice is not.
 */

/**
 * Suffixes that are themselves two labels long, so the registrable domain is
 * three: `example.co.uk`. Sorted by nothing in particular — it is a lookup.
 */
/* Platform suffixes people build sites on, where each name is a different
   owner. Not registrable in the DNS sense, but treating them as suffixes is
   what stops "every GitHub Pages site" being one case. */
const PLATFORM_SUFFIXES = [
  'github.io', 'gitlab.io', 'pages.dev', 'workers.dev', 'vercel.app', 'netlify.app',
  'herokuapp.com', 'web.app', 'firebaseapp.com', 'blogspot.com', 'wordpress.com',
  'wixsite.com', 'weebly.com', 'tumblr.com', 'myshopify.com', 'r2.dev',
];
const PLATFORM = new Set(PLATFORM_SUFFIXES);

const TWO_LABEL_SUFFIXES = new Set([
  // United Kingdom
  'co.uk', 'org.uk', 'me.uk', 'ltd.uk', 'plc.uk', 'net.uk', 'sch.uk', 'ac.uk', 'gov.uk', 'nhs.uk',
  // Australia / New Zealand
  'com.au', 'net.au', 'org.au', 'edu.au', 'gov.au', 'id.au',
  'co.nz', 'net.nz', 'org.nz', 'govt.nz', 'ac.nz', 'school.nz',
  // Japan / Korea / China / Taiwan / Hong Kong
  'co.jp', 'or.jp', 'ne.jp', 'ac.jp', 'go.jp', 'ad.jp', 'ed.jp', 'gr.jp', 'lg.jp',
  'co.kr', 'or.kr', 'ne.kr', 'go.kr', 're.kr', 'pe.kr',
  'com.cn', 'net.cn', 'org.cn', 'gov.cn', 'edu.cn', 'ac.cn',
  'com.tw', 'org.tw', 'net.tw', 'gov.tw', 'edu.tw',
  'com.hk', 'org.hk', 'net.hk', 'edu.hk', 'gov.hk',
  // Brazil and the rest of Latin America
  'com.br', 'net.br', 'org.br', 'gov.br', 'edu.br', 'art.br', 'blog.br',
  'com.ar', 'net.ar', 'org.ar', 'gob.ar', 'edu.ar',
  'com.mx', 'org.mx', 'net.mx', 'gob.mx', 'edu.mx',
  'com.co', 'net.co', 'org.co', 'gov.co', 'edu.co',
  'com.pe', 'com.ve', 'com.ec', 'com.uy', 'com.py', 'com.bo', 'com.do', 'gov.do',
  // Ukraine, Russia and neighbours
  'com.ua', 'net.ua', 'org.ua', 'gov.ua', 'edu.ua', 'in.ua', 'kiev.ua', 'kyiv.ua', 'co.ua',
  'com.ru', 'net.ru', 'org.ru', 'gov.ru', 'edu.ru', 'msk.ru', 'spb.ru',
  'com.pl', 'net.pl', 'org.pl', 'gov.pl', 'edu.pl', 'waw.pl',
  'com.tr', 'net.tr', 'org.tr', 'gov.tr', 'edu.tr',
  // India, Pakistan, Bangladesh, Indonesia, Malaysia, Philippines, Singapore
  'co.in', 'net.in', 'org.in', 'gov.in', 'edu.in', 'ac.in', 'firm.in', 'gen.in', 'ind.in',
  'com.pk', 'net.pk', 'org.pk', 'gov.pk', 'edu.pk',
  'com.bd', 'net.bd', 'org.bd', 'gov.bd', 'edu.bd',
  'co.id', 'or.id', 'ac.id', 'go.id', 'web.id', 'sch.id',
  'com.my', 'net.my', 'org.my', 'gov.my', 'edu.my',
  'com.ph', 'net.ph', 'org.ph', 'gov.ph', 'edu.ph',
  'com.sg', 'net.sg', 'org.sg', 'gov.sg', 'edu.sg',
  // Europe and the rest
  'co.za', 'org.za', 'net.za', 'gov.za', 'ac.za',
  'com.es', 'org.es', 'gob.es', 'edu.es',
  'co.il', 'org.il', 'net.il', 'gov.il', 'ac.il',
  'com.pt', 'gov.pt', 'edu.pt',
  'com.gr', 'edu.gr', 'gov.gr',
  'com.cy', 'com.mt', 'com.hr', 'com.ro', 'com.sa', 'com.eg', 'com.ng', 'com.gh', 'com.ke',
  'co.ke', 'co.th', 'in.th', 'ac.th', 'go.th',
  'co.ma', 'co.ao', 'co.mz', 'co.tz', 'co.ug', 'co.zm', 'co.zw',
  'gov.us', 'k12.ca.us',
  ...PLATFORM_SUFFIXES,
]);

/**
 * The registrable domain for a hostname, lowercased.
 *
 * Returns the input unchanged when there is nothing to reduce — a bare
 * hostname, an IP address, something that is not a domain at all — because a
 * caller grouping by this needs an answer for every row, and an empty string
 * would silently group unrelated findings together.
 */
export function registrableDomain(hostname: string): string {
  const host = hostname
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .split('/')[0]
    .split(':')[0]
    .replace(/\.$/, '');

  if (!host) return hostname.trim().toLowerCase();
  // An IPv4 address has no registrable domain; reducing it would turn every
  // host in a /16 into one "case".
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return host;

  const labels = host.split('.').filter(Boolean);
  if (labels.length <= 2) return host;

  const lastTwo = labels.slice(-2).join('.');
  if (TWO_LABEL_SUFFIXES.has(lastTwo)) {
    return labels.slice(-3).join('.');
  }
  return lastTwo;
}

/**
 * The name a registry actually holds: `anonlink.netlify.app` is the site's own
 * case, but the registry only knows `netlify.app`, and asked about the site it
 * answers 400. For RDAP and WHOIS, never for grouping cases.
 */
export function registeredDomain(hostname: string): string {
  const registrable = registrableDomain(hostname);
  const lastTwo = registrable.split('.').slice(-2).join('.');
  return PLATFORM.has(lastTwo) ? lastTwo : registrable;
}

/** Is this hostname a subdomain of its own registrable domain? */
export function isSubdomain(hostname: string): boolean {
  const host = hostname.trim().toLowerCase().replace(/\.$/, '');
  return host !== registrableDomain(host);
}

/** The two-label suffixes {@link domainStem} cuts whole, for a query that
 *  ranks families in the database (`= ANY`, which a regex of 200 is not). */
export const TWO_LABEL_SUFFIX_LIST: readonly string[] = [...TWO_LABEL_SUFFIXES];

/** A registrable domain's name with its TLD/suffix cut off: `limetorrents.lol`,
 *  `.info` and `.cc` all return `limetorrents`. Groups a name's TLD variants
 *  on the Domains info screen — {@link isSubdomain} is the other axis. */
export function domainStem(hostname: string): string {
  const registrable = registrableDomain(hostname);
  const labels = registrable.split('.').filter(Boolean);
  if (labels.length <= 1) return registrable;

  const lastTwo = labels.slice(-2).join('.');
  if (TWO_LABEL_SUFFIXES.has(lastTwo)) return labels.slice(0, -2).join('.') || registrable;
  return labels.slice(0, -1).join('.') || registrable;
}
